import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { startWith, map, switchMap, filter, takeUntil, debounceTime } from 'rxjs/operators';
import { format } from 'date-fns';
import { AdhocServiceService } from '../../services/adhoc-service.service';
import { HelperService } from '../../services/helper.service';
import { ThemeService } from '../../services/theme.service';
import { ClientService } from '../../services/client.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { ClientSelectDialogComponent } from '../client-select-dialog/client-select-dialog.component';
import { StaffSelectDialogComponent } from '../staff-select-dialog/staff-select-dialog.component';
import { FundingSelectDialogComponent } from '../funding-select-dialog/funding-select-dialog.component';
import { ActivitySelectDialogComponent, ActivitySelectResult } from '../activity-select-dialog/activity-select-dialog.component';
import {
  AdhocClient,
  ActivityGroup,
  Carer,
  CommonActivity,
  FundingSource,
  ExistingService,
  AdhocActivity,
  DurationCount,
  ClientAdhocSummary
} from '../../models/adhoc-service.model';

@Component({
  selector: 'app-adhoc-service',
  templateUrl: './adhoc-service.component.html',
  styleUrls: ['./adhoc-service.component.scss']
})
export class AdhocServiceComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Data
  clients: AdhocClient[] = [];
  activityGroups: ActivityGroup[] = [];
  commonActivities: CommonActivity[] = [];
  carers: Carer[] = [];
  existingServices: ExistingService[] = [];
  fundingSources: FundingSource[] = [];
  recommendedBudgetTypeId = '';
  isHpac = false;

  // Adhoc-specific data
  adhocCommonActivities: AdhocActivity[] = [];
  globalDurations: DurationCount[] = [];
  clientAdhocMap: Map<string, ClientAdhocSummary> = new Map();
  clientTopActivities: AdhocActivity[] = [];
  hasClientHistory = false;
  activityDurationMap: Map<string, number> = new Map();

  // Loading states
  isLoading = true;
  isLoadingServices = false;
  isLoadingFunding = false;
  isSubmitting = false;
  fundingPreviewed = false;

  // Funding edit state
  fundingEdited = false;

  // Form controls
  clientControl = new FormControl<AdhocClient | string | null>(null, Validators.required);
  dateControl = new FormControl<Date>(new Date(), Validators.required);
  timeControl = new FormControl<string>('', Validators.required);
  activityControl = new FormControl<string>('', Validators.required);
  durationControl = new FormControl<number | null>(null, Validators.required);
  staffControl = new FormControl<Carer | string | null>(null);
  notesControl = new FormControl<string>('');
  selectedFundingBudgetTypeId = '';

  // Autocomplete filtered lists
  filteredClients$!: Observable<AdhocClient[]>;
  filteredStaffGroups$!: Observable<{ matching: Carer[], other: Carer[] }>;

  // Activity selection
  selectedActivityRoleIds: string[] = [];

  // Duration picker
  durationPresets = [15, 30, 60, 90, 120];
  customDurationValue: number | null = null;

  // Cost summary (placeholder)
  unitPrice = 2;

  // Recent clients
  private readonly RECENT_CLIENTS_KEY = 'adhoc_recent_clients';
  private readonly MAX_RECENT_CLIENTS = 5;
  recentClients: AdhocClient[] = [];
  recentClientPhotos: Map<number, string> = new Map();

  // Selected client photo
  selectedClientPhotoUrl: string | null = null;

  // Time input
  timeInputRaw = '';
  isEditingTime = false;

  constructor(
    private adhocService: AdhocServiceService,
    private helperService: HelperService,
    private themeService: ThemeService,
    private clientService: ClientService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadData();
    this.setupAutocomplete();
    this.setupExistingServicesWatcher();
    this.loadRecentClients();
    this.durationControl.setValue(15);
    this.timeControl.setValue('09:00');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.selectedClientPhotoUrl) {
      URL.revokeObjectURL(this.selectedClientPhotoUrl);
    }
    this.recentClientPhotos.forEach(url => URL.revokeObjectURL(url));
  }

  private loadData(): void {
    this.isLoading = true;

    this.adhocService.getClients().subscribe({
      next: (res) => {
        this.clients = this.sortByName(res.clients || [], client => `${client.LastName} ${client.FirstName}`);
        this.clientsLoaded = true;
        this.mergeAdhocCounts();
        this.checkLoadingComplete();
      },
      error: () => { this.clientsLoaded = true; this.checkLoadingComplete(); }
    });

    this.adhocService.getActivities().subscribe({
      next: (res) => {
        this.activityGroups = res.activities ? Object.values(res.activities) : [];
        this.checkLoadingComplete();
      },
      error: () => this.checkLoadingComplete()
    });

    this.adhocService.getCarers().subscribe({
      next: (res) => {
        this.carers = this.sortByName(
          res.carers?.by_carer_id ? Object.values(res.carers.by_carer_id) : [],
          carer => `${carer.LastName} ${carer.FirstName}`
        );
        this.checkLoadingComplete();
      },
      error: () => this.checkLoadingComplete()
    });

    this.adhocService.getCommonActivities().subscribe({
      next: (res) => {
        this.commonActivities = res.common_activities || [];
        this.checkLoadingComplete();
      },
      error: () => this.checkLoadingComplete()
    });

    this.adhocService.getAdhocCommonActivities().subscribe({
      next: (res) => {
        this.adhocCommonActivities = res.activities || [];
        this.globalDurations = res.durations || [];
        // Build duration map from global adhoc activities
        for (const activity of this.adhocCommonActivities) {
          if (activity.most_common_duration != null) {
            this.activityDurationMap.set(activity.activity_id, activity.most_common_duration);
          }
        }
        this.checkLoadingComplete();
      },
      error: () => this.checkLoadingComplete()
    });

    this.adhocService.getClientAdhocSummaries().subscribe({
      next: (res) => {
        const clients = res.clients || [];
        for (const summary of clients) {
          this.clientAdhocMap.set(String(summary.resident_id), summary);
        }
        this.summariesLoaded = true;
        this.mergeAdhocCounts();
        this.checkLoadingComplete();
      },
      error: () => { this.summariesLoaded = true; this.checkLoadingComplete(); }
    });
  }

  private loadCount = 0;
  private clientsLoaded = false;
  private summariesLoaded = false;

  private sortByName<T>(items: T[], getName: (item: T) => string): T[] {
    return [...items].sort((a, b) => getName(a).localeCompare(getName(b), undefined, { sensitivity: 'base' }));
  }

  private mergeAdhocCounts(): void {
    if (!this.clientsLoaded || !this.summariesLoaded) return;
    for (const client of this.clients) {
      const summary = this.clientAdhocMap.get(String(client.ResidentID));
      client.adhoc_count = summary?.adhoc_count || 0;
    }
  }

  private checkLoadingComplete(): void {
    this.loadCount++;
    if (this.loadCount >= 6) {
      this.isLoading = false;
    }
  }

  private setupAutocomplete(): void {
    this.filteredClients$ = this.clientControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
        return this.clients.filter(c =>
          `${c.LastName}, ${c.FirstName}`.toLowerCase().includes(filterValue) ||
          c.Customer_Code.toLowerCase().includes(filterValue)
        );
      })
    );

    this.filteredStaffGroups$ = combineLatest([
      this.staffControl.valueChanges.pipe(startWith('')),
      this.activityControl.valueChanges.pipe(startWith(''))
    ]).pipe(
      map(([staffValue]) => {
        const filterValue = typeof staffValue === 'string' ? staffValue.toLowerCase() : '';
        const filtered = this.carers.filter(c =>
          `${c.LastName}, ${c.FirstName}`.toLowerCase().includes(filterValue)
        );

        if (this.selectedActivityRoleIds.length === 0) {
          return { matching: filtered, other: [] };
        }

        const matching: Carer[] = [];
        const other: Carer[] = [];
        for (const carer of filtered) {
          const carerRoles = carer.rs_role_ids || [];
          if (carerRoles.some(r => this.selectedActivityRoleIds.includes(r))) {
            matching.push(carer);
          } else {
            other.push(carer);
          }
        }
        return { matching, other };
      })
    );
  }

  private setupExistingServicesWatcher(): void {
    combineLatest([
      this.clientControl.valueChanges.pipe(startWith(this.clientControl.value)),
      this.dateControl.valueChanges.pipe(startWith(this.dateControl.value))
    ]).pipe(
      debounceTime(300),
      filter(([client, date]) => client !== null && typeof client === 'object' && date !== null),
      switchMap(([client, date]) => {
        this.isLoadingServices = true;
        const dateStr = format(date!, 'yyyy-MM-dd');
        return this.adhocService.getTimeline(dateStr).pipe(
          map(response => {
            const selectedClient = client as AdhocClient;
            const gridData = response?.gridData || [];
            return gridData
              .filter((item: any) => item.ResidentID === selectedClient.ResidentID)
              .map((item: any) => ({
                start_time: item.start_time || format(new Date(item.start_date), 'HH:mm'),
                end_time: item.end_time || format(new Date(item.end_date), 'HH:mm'),
                activity_description: item.text || item.activity_description || '',
                staff_name: item.staff_name || item.carer_name || '',
                ResidentID: item.ResidentID
              }));
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (services) => {
        this.existingServices = services;
        this.isLoadingServices = false;
      },
      error: () => {
        this.existingServices = [];
        this.isLoadingServices = false;
      }
    });
  }


  displayClientFn(client: AdhocClient | null): string {
    return client ? `${client.LastName}, ${client.FirstName}` : '';
  }

  displayStaffFn(carer: Carer | null): string {
    return carer ? `${carer.LastName}, ${carer.FirstName}` : '';
  }

  clearSelectedActivity(): void {
    this.activityControl.reset();
    this.selectedActivityRoleIds = [];
  }

  private selectAdhocActivity(activity: AdhocActivity): void {
    this.activityControl.setValue(activity.activity_id);
    this.selectedActivityRoleIds = [];
    this.applyDurationForActivity(activity.activity_id);
  }

  private applyDurationForActivity(activityId: string): void {
    const duration = this.activityDurationMap.get(activityId);
    if (duration != null) {
      this.durationControl.setValue(duration);
      if (this.durationPresets.includes(duration)) {
        this.customDurationValue = null;
      } else {
        this.customDurationValue = duration;
      }
    }
  }

  changeActivity(): void {
    this.clearSelectedActivity();
    this.openActivitySelectDialog();
  }

  openActivitySelectDialog(): void {
    const dialogRef = this.dialog.open(ActivitySelectDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      data: {
        commonActivities: this.commonActivities,
        activityGroups: this.activityGroups,
        adhocCommonActivities: this.adhocCommonActivities,
        clientTopActivities: this.clientTopActivities,
        activityDurationMap: this.activityDurationMap
      },
      panelClass: 'activity-select-dialog-panel',
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe((result: ActivitySelectResult | undefined) => {
      if (result) {
        this.activityControl.setValue(result.activity_id);
        this.selectedActivityRoleIds = result.roles;
        if (result.duration != null) {
          this.durationControl.setValue(result.duration);
          if (this.durationPresets.includes(result.duration)) {
            this.customDurationValue = null;
          } else {
            this.customDurationValue = result.duration;
          }
        }
      }
    });
  }

  getSelectedActivityDescription(): string {
    const val = this.activityControl.value;
    if (!val) return '';

    // Search adhoc activities (client-specific and global)
    const adhoc = [...this.clientTopActivities, ...this.adhocCommonActivities].find(a => a.activity_id === val);
    if (adhoc) return adhoc.activity_name;

    // Search common activities
    const common = this.commonActivities.find(a => a.activity_detail_id === val);
    if (common) return common.description;

    // Search activity groups
    for (const group of this.activityGroups) {
      const child = group.activities.find(a => a.id === val);
      if (child) return child.description;
    }
    return '';
  }

  // Duration picker
  selectDurationPreset(minutes: number): void {
    this.customDurationValue = null;
    this.durationControl.setValue(minutes);
  }

  onCustomDurationChange(): void {
    if (this.customDurationValue && this.customDurationValue >= 1) {
      this.durationControl.setValue(this.customDurationValue);
    } else {
      this.durationControl.reset();
    }
  }

  isDurationPresetSelected(minutes: number): boolean {
    return this.durationControl.value === minutes && !this.customDurationValue;
  }

  isClientSelected(): boolean {
    return this.clientControl.value !== null && typeof this.clientControl.value === 'object';
  }

  getSelectedClient(): AdhocClient | null {
    const val = this.clientControl.value;
    return (val && typeof val === 'object') ? val as AdhocClient : null;
  }

  onClientSelected(): void {
    // Reset funding
    this.fundingSources = [];
    this.fundingPreviewed = false;
    this.selectedFundingBudgetTypeId = '';
    this.fundingEdited = false;

    // Load photo
    this.loadSelectedClientPhoto();

    const client = this.clientControl.value as AdhocClient;

    // Auto-load funding
    this.isLoadingFunding = true;
    this.adhocService.getFundingPreview(client.ResidentID).subscribe({
      next: (res) => {
        this.fundingSources = res.funding_sources || [];
        this.recommendedBudgetTypeId = res.recommended_budget_type_id || '';
        this.isHpac = res.is_hpac || false;
        this.selectedFundingBudgetTypeId = this.recommendedBudgetTypeId;
        this.fundingPreviewed = true;
        this.fundingEdited = false;
        this.isLoadingFunding = false;
      },
      error: () => {
        this.isLoadingFunding = false;
        this.helperService.openSnackBar('Failed to load funding preview', 'Close');
      }
    });

    // Load client-specific activities
    const summary = this.clientAdhocMap.get(String(client.ResidentID));
    if (summary && summary.top_activities && summary.top_activities.length > 0) {
      this.clientTopActivities = summary.top_activities;
      this.hasClientHistory = true;
      // Merge client activity durations into the map
      for (const activity of summary.top_activities) {
        if (activity.most_common_duration != null) {
          this.activityDurationMap.set(activity.activity_id, activity.most_common_duration);
        }
      }
      // Auto-select top activity if it has >3 uses
      if (this.clientTopActivities[0].count > 3) {
        this.selectAdhocActivity(this.clientTopActivities[0]);
      }
    } else {
      this.clientTopActivities = [];
      this.hasClientHistory = false;
    }
  }

  clearSelectedClient(): void {
    this.clientControl.reset();
    if (this.selectedClientPhotoUrl) {
      URL.revokeObjectURL(this.selectedClientPhotoUrl);
      this.selectedClientPhotoUrl = null;
    }
    this.fundingSources = [];
    this.fundingPreviewed = false;
    this.selectedFundingBudgetTypeId = '';
    this.fundingEdited = false;
    this.clientTopActivities = [];
    this.hasClientHistory = false;
  }

  selectRecentClient(client: AdhocClient): void {
    const fullClient = this.clients.find(c => c.ResidentID === client.ResidentID) || client;
    this.clientControl.setValue(fullClient);
    this.onClientSelected();
  }

  changeClient(): void {
    this.clearSelectedClient();
    this.openClientSelectDialog();
  }

  openClientSelectDialog(): void {
    const dialogRef = this.dialog.open(ClientSelectDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      data: { clients: this.clients },
      panelClass: 'client-select-dialog-panel',
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe((client: AdhocClient | undefined) => {
      if (client) {
        this.clientControl.setValue(client);
        this.onClientSelected();
      }
    });
  }

  changeStaff(): void {
    this.clearSelectedStaff();
    this.openStaffSelectDialog();
  }

  openStaffSelectDialog(): void {
    const dialogRef = this.dialog.open(StaffSelectDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      data: {
        staff: this.carers,
        requiredRoleIds: this.selectedActivityRoleIds.length > 0 ? this.selectedActivityRoleIds : undefined
      },
      panelClass: 'staff-select-dialog-panel',
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe((staff: Carer | undefined) => {
      if (staff) {
        this.staffControl.setValue(staff);
      }
    });
  }

  getSelectedStaff(): Carer | null {
    const val = this.staffControl.value;
    return (val && typeof val === 'object') ? val as Carer : null;
  }

  isStaffSelected(): boolean {
    return this.staffControl.value !== null && typeof this.staffControl.value === 'object';
  }

  clearSelectedStaff(): void {
    this.staffControl.reset();
  }

  // Funding edit/revert
  onEditFunding(): void {
    const dialogRef = this.dialog.open(FundingSelectDialogComponent, {
      width: '480px',
      maxWidth: '95vw',
      data: {
        fundingSources: this.fundingSources,
        selectedBudgetTypeId: this.selectedFundingBudgetTypeId,
        recommendedBudgetTypeId: this.recommendedBudgetTypeId,
        isHpac: this.isHpac
      },
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe((budgetTypeId: string | undefined) => {
      if (budgetTypeId !== undefined) {
        this.selectedFundingBudgetTypeId = budgetTypeId;
        this.fundingEdited = budgetTypeId !== this.recommendedBudgetTypeId;
      }
    });
  }

  onRevertFunding(): void {
    this.selectedFundingBudgetTypeId = this.recommendedBudgetTypeId;
    this.fundingEdited = false;
  }

  getSelectedFundingDescription(): string {
    const source = this.fundingSources.find(fs => fs.BudgetTypeID === this.selectedFundingBudgetTypeId);
    return source ? source.Description : '';
  }

  // Cost summary
  getCostTotal(): number {
    return this.unitPrice * (this.durationControl.value || 0);
  }

  private loadRecentClients(): void {
    try {
      const stored = localStorage.getItem(this.RECENT_CLIENTS_KEY);
      if (stored) {
        this.recentClients = JSON.parse(stored);
      }
      this.recentClients.forEach(client => this.loadRecentClientPhoto(client));
    } catch {
      this.recentClients = [];
    }
  }

  private saveRecentClient(client: AdhocClient): void {
    const entry = {
      ResidentID: client.ResidentID,
      FirstName: client.FirstName,
      LastName: client.LastName,
      Customer_Code: client.Customer_Code,
      Address1: client.Address1,
      Status: client.Status
    };
    this.recentClients = [
      entry,
      ...this.recentClients.filter(c => c.ResidentID !== client.ResidentID)
    ].slice(0, this.MAX_RECENT_CLIENTS);
    try {
      localStorage.setItem(this.RECENT_CLIENTS_KEY, JSON.stringify(this.recentClients));
    } catch { }
  }

  private loadSelectedClientPhoto(): void {
    const client = this.clientControl.value;
    if (!client || typeof client === 'string') return;

    if (this.selectedClientPhotoUrl) {
      URL.revokeObjectURL(this.selectedClientPhotoUrl);
      this.selectedClientPhotoUrl = null;
    }

    this.clientService.getClientPhoto(String(client.ResidentID)).subscribe({
      next: (blob) => {
        this.selectedClientPhotoUrl = URL.createObjectURL(blob);
      },
      error: () => {
        this.selectedClientPhotoUrl = null;
      }
    });
  }

  private loadRecentClientPhoto(client: AdhocClient): void {
    this.clientService.getClientPhoto(String(client.ResidentID)).subscribe({
      next: (blob) => {
        this.recentClientPhotos.set(client.ResidentID, URL.createObjectURL(blob));
      },
      error: () => { }
    });
  }

  getRecentClientPhoto(residentId: number): string | null {
    return this.recentClientPhotos.get(residentId) || null;
  }

  // Time input
  onTimeFocus(event: Event): void {
    this.isEditingTime = true;
    this.timeInputRaw = '';
    const input = event.target as HTMLInputElement;
    setTimeout(() => input.select());
  }

  onTimeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^\d:]/g, '');
    this.timeInputRaw = input.value;
  }

  onTimeBlur(): void {
    this.isEditingTime = false;
    this.formatAndSetTime();
  }

  onTimeKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      (event.target as HTMLInputElement).blur();
    }
  }

  private formatAndSetTime(): void {
    const raw = this.timeInputRaw.replace(/\D/g, '');
    if (raw.length === 0) return;

    let hours: number;
    let mins: number;

    if (raw.length <= 2) {
      hours = parseInt(raw, 10);
      mins = 0;
    } else if (raw.length === 3) {
      hours = parseInt(raw.substring(0, 1), 10);
      mins = parseInt(raw.substring(1), 10);
    } else {
      hours = parseInt(raw.substring(0, 2), 10);
      mins = parseInt(raw.substring(2, 4), 10);
    }

    if (hours >= 0 && hours <= 23 && mins >= 0 && mins <= 59) {
      this.timeControl.setValue(`${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`);
      this.timeControl.setErrors(null);
    } else {
      this.timeControl.setErrors({ invalidTime: true });
    }
  }

  isFormValid(): boolean {
    const clientValid = this.clientControl.valid && typeof this.clientControl.value === 'object';
    return clientValid &&
      this.dateControl.valid &&
      this.timeControl.valid &&
      this.activityControl.valid &&
      this.durationControl.valid &&
      !this.isSubmitting;
  }

  getSubmitTooltip(): string {
    const missing: string[] = [];
    if (!this.clientControl.valid || typeof this.clientControl.value !== 'object') missing.push('Client');
    if (!this.dateControl.valid) missing.push('Date');
    if (!this.timeControl.valid) missing.push('Time');
    if (!this.activityControl.valid) missing.push('Activity');
    if (!this.durationControl.valid) missing.push('Duration');
    return missing.length > 0 ? `Missing: ${missing.join(', ')}` : '';
  }

  onSubmit(): void {
    if (!this.isFormValid()) {
      this.markFormTouched();
      return;
    }

    const client = this.clientControl.value as AdhocClient;
    const date = this.dateControl.value!;
    const staff = this.getSelectedStaff();

    const request: any = {
      client_id: String(client.ResidentID),
      activity_detail_id: this.activityControl.value!,
      start_date: format(date, 'dd MMM yyyy'),
      start_time: this.timeControl.value!,
      duration_minutes: this.durationControl.value!,
      ...(staff ? { staff_id: staff.CarerID } : {}),
      ...(this.selectedFundingBudgetTypeId ? { budget_type_id: this.selectedFundingBudgetTypeId } : {}),
      ...(this.notesControl.value ? { notes: this.notesControl.value.trim() } : {})
    };

    this.isSubmitting = true;
    this.helperService.openProgressSnackBar('Submitting to eCase...');

    this.adhocService.createAdhocService(request).subscribe({
      next: (res) => {
        this.helperService.closeProgressSnackBar();
        this.isSubmitting = false;

        if (res.status === 'success') {
          this.saveRecentClient(client);
          const costDisplay = res.actual_cost != null ? `$${res.actual_cost.toFixed(2)}` : 'N/A';
          const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
              title: 'Service Created',
              message: `Ad-hoc service created successfully.\n\nFunding Source: ${res.funding_source || 'Auto-selected'}\nCost: ${costDisplay}`,
              confirmText: 'Close'
            }
          });

          dialogRef.afterClosed().subscribe(() => {
            this.resetForm();
          });
        } else {
          this.helperService.openSnackBar(res.message || 'An error occurred', 'Close');
        }
      },
      error: (err) => {
        this.helperService.closeProgressSnackBar();
        this.isSubmitting = false;
        const message = err?.error?.message || err?.message || 'Failed to create ad-hoc service';
        this.helperService.openSnackBar(message, 'Close');
      }
    });
  }

  shouldShowError(control: FormControl<unknown>): boolean {
    return control.invalid && (control.dirty || control.touched);
  }

  private markFormTouched(): void {
    this.clientControl.markAsTouched();
    this.dateControl.markAsTouched();
    this.timeControl.markAsTouched();
    this.activityControl.markAsTouched();
    this.durationControl.markAsTouched();
  }

  private resetForm(): void {
    this.clientControl.reset();
    if (this.selectedClientPhotoUrl) {
      URL.revokeObjectURL(this.selectedClientPhotoUrl);
      this.selectedClientPhotoUrl = null;
    }
    this.dateControl.setValue(new Date());
    this.timeControl.setValue('09:00');
    this.activityControl.reset();
    this.selectedActivityRoleIds = [];
    this.durationControl.setValue(15);
    this.customDurationValue = null;
    this.staffControl.reset();
    this.notesControl.reset();
    this.fundingSources = [];
    this.fundingPreviewed = false;
    this.selectedFundingBudgetTypeId = '';
    this.fundingEdited = false;
    this.existingServices = [];
    this.isHpac = false;
    this.clientTopActivities = [];
    this.hasClientHistory = false;
  }
}
